import { IonToast } from "@ionic/react";
import { createContext, useCallback, useContext, useState } from "react";
import "./ToastStyles.css";
import { getToastIcon } from "../utils/icons";

export type ToastType = "success" | "error" | "warning" | "info";

export type ToastButton = {
  text: string;
  role?: "cancel" | "destructive";
  handler?: () => boolean | void | Promise<boolean | void>;
};

export type ToastOptions = {
  buttons?: ToastButton[];
  header?: string;
  dedupeKey?: string;
};

type Toast = {
  id: number;
  message: string;
  duration: number;
  type: ToastType;
  buttons?: ToastButton[];
  header?: string;
  dedupeKey?: string;
};

type ToastContextType = {
  addToast: (
    message: string,
    type: ToastType,
    duration?: number,
    options?: ToastOptions
  ) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

type ToastProviderProps = {
  children: React.ReactNode;
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (
      message: string,
      type: ToastType,
      duration?: number,
      options?: ToastOptions
    ) => {
      const id = Date.now();
      // Longer default when the toast carries buttons — 3s is not enough
      // to read a message AND tap an action. Explicit duration (incl. 0)
      // still wins, so persistent toasts keep working.
      const resolvedDuration =
        duration ?? (options?.buttons?.length ? 10000 : 3000);
      setToasts((prev) => {
        // Suppress duplicates for callers that opt in via dedupeKey — e.g.
        // repeated Sign In taps that would otherwise stack identical toasts.
        if (
          options?.dedupeKey &&
          prev.some((t) => t.dedupeKey === options.dedupeKey)
        ) {
          return prev;
        }
        return [
          ...prev,
          {
            id,
            message,
            duration: resolvedDuration,
            type,
            buttons: options?.buttons,
            header: options?.header,
            dedupeKey: options?.dedupeKey,
          },
        ];
      });
    },
    []
  );

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {toasts.map((toast, i) => (
        <IonToast
          cssClass={
            toast.buttons?.length
              ? "fh-toast fh-toast--with-buttons"
              : "fh-toast"
          }
          key={toast.id}
          isOpen={true}
          header={toast.header}
          message={toast.message}
          duration={toast.duration}
          onDidDismiss={() => removeToast(toast.id)}
          position="top"
          data-testid={`toast-message-${i + 1}`}
          icon={getToastIcon(toast.type)}
          buttons={toast.buttons}
        />
      ))}
    </ToastContext.Provider>
  );
};
